import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "inmind:token";

export const getToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
export const setToken = (t) => { try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } };

const client = axios.create({ baseURL: API, timeout: 60000, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default client;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ----- Auth -----
export async function apiRegister(payload) {
  const { data } = await client.post("/auth/register", payload);
  setToken(data.token);
  return data.user;
}
export async function apiLogin(payload) {
  const { data } = await client.post("/auth/login", payload);
  setToken(data.token);
  return data.user;
}
export async function apiGoogleSession(session_id) {
  const { data } = await client.post("/auth/google/session", { session_id });
  if (data.token) setToken(data.token);
  return data.user;
}
export const apiMe = () => client.get("/auth/me").then((r) => r.data);
export async function apiLogout() {
  try { await client.post("/auth/logout"); } catch { /* ignore */ }
  setToken(null);
}

// ----- Preferences -----
export const getPrefs = () => client.get("/me/preferences").then((r) => r.data);
export const updatePrefs = (p) => client.patch("/me/preferences", p).then((r) => r.data);
export const listCategories = () => client.get("/me/categories").then((r) => r.data);
export const changePassword = (body) => client.post("/me/password", body).then((r) => r.data);

// ----- Today / Library / Term -----
export const fetchToday = () => client.get("/me/today").then((r) => r.data);
export const generateToday = () => client.post("/me/today/generate").then((r) => r.data);
export const fetchPublicToday = () => client.get("/public/today").then((r) => r.data);
export const fetchMyTerms = (params = {}) => client.get("/me/terms", { params }).then((r) => r.data);
export const fetchTerm = (id) => client.get(`/me/terms/${id}`).then((r) => r.data);
export const reviewTerm = (id, status) => client.patch(`/me/terms/${id}/review`, { status }).then((r) => r.data);

// ----- Per-term notes -----
export const listNotes = (term_id) => client.get("/me/notes", { params: { term_id } }).then((r) => r.data);
export const createNote = (term_id, content) => client.post("/me/notes", { term_id, content }).then((r) => r.data);
export const updateNote = (id, content) => client.patch(`/me/notes/${id}`, { content }).then((r) => r.data);
export const deleteNote = (id) => client.delete(`/me/notes/${id}`).then((r) => r.data);

// ----- Personal notes (global) -----
export const listPersonalNotes = (params = {}) => client.get("/me/personal-notes", { params }).then((r) => r.data);
export const listPersonalNoteTags = () => client.get("/me/personal-notes/tags").then((r) => r.data);
export const getPersonalNote = (id) => client.get(`/me/personal-notes/${id}`).then((r) => r.data);
export const createPersonalNote = (payload) => client.post("/me/personal-notes", payload).then((r) => r.data);
export const updatePersonalNote = (id, payload) => client.patch(`/me/personal-notes/${id}`, payload).then((r) => r.data);
export const deletePersonalNote = (id) => client.delete(`/me/personal-notes/${id}`).then((r) => r.data);
export const getPersonalNoteThink = (id) => client.get(`/me/personal-notes/${id}/think`).then((r) => r.data);
export const sendPersonalNoteThink = (id, content) =>
  client.post(`/me/personal-notes/${id}/think/messages`, { content }).then((r) => r.data);
export const clearPersonalNoteThink = (id) => client.delete(`/me/personal-notes/${id}/think`).then((r) => r.data);

// ----- Stats -----
export const fetchMyStats = () => client.get("/me/stats").then((r) => r.data);

// ----- Think (AI helper per term) -----
export const getThinkChat = (term_id) => client.get(`/me/think/${term_id}`).then((r) => r.data);
export const sendThinkMessage = (term_id, content) =>
  client.post(`/me/think/${term_id}/messages`, { content }).then((r) => r.data);
export const clearThinkChat = (term_id) => client.delete(`/me/think/${term_id}`).then((r) => r.data);

// ----- Admin -----
export const adminStats = () => client.get("/admin/stats").then((r) => r.data);
export const adminUsers = () => client.get("/admin/users").then((r) => r.data);
export const adminDeleteUser = (id) => client.delete(`/admin/users/${id}`).then((r) => r.data);
export const adminActivity = () => client.get("/admin/activity").then((r) => r.data);
export const adminTerms = () => client.get("/admin/terms").then((r) => r.data);
export const adminUpdateTerm = (id, body) => client.patch(`/admin/terms/${id}`, body).then((r) => r.data);
export const adminDeleteTerm = (id) => client.delete(`/admin/terms/${id}`).then((r) => r.data);
export const adminGenerateTerm = (category) =>
  client.post("/admin/terms/generate", null, { params: category ? { category } : {} }).then((r) => r.data);
export const adminPopularTerms = () => client.get("/admin/popular-terms").then((r) => r.data);
